import React, { useEffect, useMemo, useState } from "react";
import { BsSearch } from "react-icons/bs";
import html2pdf from "html2pdf.js";
import TablePagination from "@mui/material/TablePagination";
import ReLoginModal from "../../components/ReLoginModal";
import * as XLSX from "xlsx";  // Import the xlsx library
import "../../Styles/invoiceTable.css";
import { CircularProgress } from "@mui/material";
import api from "../../util/axios";

type BankReport = {
  _id: string;
  businessRef: string;
  branchId: string;
  routeType: string;
  details: string;
  customer: {
    _id: string;
    businessName: string;
  };
  customerAccount: {
    _id: string;
    balance: {
      amount: number;
      quantity: number;
    };
  };
  bank: {
    _id: string;
    bankName: string;
  };
  balance: {
    previous: number;
    current: number;
  };
  staff: {
    _id: string;
    name: string;
    role: string;
  };
  amountIn: number;
  amountOut: number;
  createdAt: string;
  updatedAt: string;
  __v: number;
};

const BankReport = () => {
  const [getBankReport, setGetBankReport] = useState<BankReport[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [page, setPage] = useState(0);
  const [reLogin, setReLogin] = useState(false);
  const token = window.localStorage.getItem("token");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [bankQuery, setBankQuery] = useState("");
  const [businessQuery, setBusinessQuery] = useState("");
  const [searchField, setSearchField] = useState("businessName");

  function queryBankReport(
    pageNumber: number,
    pageSize: number,
    fromDate: string,
    toDate: string
  ) {
    setIsLoading(true);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const queryParams = `?limit=${pageSize}&startDate=${fromDate}&endDate=${toDate}&page=${pageNumber}`;

    return api
      .get(`/settings/bank/Report${queryParams}`, {
        headers: headers,
      })
      .then((res) => {
        setGetBankReport(res.data.data.bankReport);
      })
      .catch((error) => {
        if (
          error.response.data.message === "jwt expired" ||
          error.response.data.message === "jwt malformed"
        ) {
           
          setReLogin(true);
        } else {
          setReLogin(false);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    queryBankReport(page + 1, rowsPerPage, fromDate, toDate);
  }, [page, rowsPerPage, fromDate, toDate]);

  const filteredBankReports = useMemo(() => {
    return getBankReport.filter((report) => {
      const reportDate = new Date(report.createdAt);
      const startDate = fromDate ? new Date(fromDate) : null;
      const endDate = toDate ? new Date(toDate) : null;

      if (startDate && reportDate < startDate) return false;
      if (endDate && reportDate > endDate) return false;

      if (searchField === "combined") {
        if (!businessQuery && !bankQuery) return true;
      } else if (!searchQuery) {
        return true;
      }

      const businessName = report.customer?.businessName.toLowerCase() || "";
      const bankName = report.bank?.bankName.toLowerCase() || "";

      const businessNameMatch = businessName.includes(
        searchField === "combined" ? businessQuery.toLowerCase() : searchQuery.toLowerCase()
      );
      const bankNameMatch = bankName.includes(
        searchField === "combined" ? bankQuery.toLowerCase() : searchQuery.toLowerCase()
      );
      switch (searchField) {
        case "businessName":
          return businessNameMatch;
        case "bankName":
          return bankNameMatch;
        case "combined":
          return businessNameMatch && bankNameMatch;
        default:
          return businessNameMatch;
      }
    });
  }, [getBankReport, fromDate, toDate, searchQuery, businessQuery, bankQuery, searchField]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const generatePDF = () => {
    const slipElement = document.querySelector(".invoiceTable");

    if (slipElement) {
      const pdfOptions = {
        margin: 2,
        filename: "Bank-Report.pdf",
        image: { type: "png" },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      };
      html2pdf().from(slipElement).set(pdfOptions).save();
    }
  };

  const exportToExcel = () => {
    const filteredData = filteredBankReports.map((report, inx) => {
      const reportDate = new Date(report?.createdAt);
      const formattedDate = reportDate.toLocaleString();

      return {
        "S/N": inx + 1,
        "Business Name": report?.customer?.businessName,
        "Bank Name": report?.bank?.bankName,
        "Amount In": report?.amountIn,
        "Amount Out": report?.amountOut,
        "Previous Balance": report?.balance?.previous,
        "Current Balance": report?.balance?.current,
        "Details": report?.details,
        "Created By": report?.staff?.name,
        "Date": formattedDate,
      };
    });

    const ws = XLSX.utils.json_to_sheet(filteredData);
    ws['!cols'] = [
      { width: 5 },
      { width: 25 },
      { width: 20 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BankReports");

    XLSX.writeFile(wb, "Bank-Report.xlsx");
  };

  return (
    <div className="board">
      {reLogin && <ReLoginModal />}
      <div className="method">
        <div className="searchFieldSelect">
          <label htmlFor="filter">Search filter: </label>
          <select
            id="filter"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            <option value="businessName">Customer Name</option>
            <option value="bankName">Bank Name</option>
            <option value="combined">Mixed search</option>
          </select>
        </div>
        {searchField === "combined" ? (
          <div className="combinedSearch">
            <input
              type="text"
              placeholder="Customer Name"
              value={businessQuery}
              onChange={(e) => setBusinessQuery(e.target.value)}
            />
            <input
              type="text"
              placeholder="Bank Name"
              value={bankQuery}
              onChange={(e) => setBankQuery(e.target.value)}
            />
          </div>
        ) : (
          <div className="apInput">
            <input
              type="text"
              value={searchQuery}
              placeholder="search"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <BsSearch className="apIcon" />
          </div>
        )}
        <div className="filterBy">
          Filter By:
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>
      <div className="invoiceTable">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h2>Please wait while we process the request...</h2>
          </div>
        ) : filteredBankReports.length > 0 ? (
          <table className="inTable">
            <thead>
              <tr className="heTable">
                <th>S/N</th>
                <th>Customer Name</th>
                <th>Bank Name</th>
                <th className="smm">Amount In</th>
                <th className="smm">Amount Out</th>
                <th className="smm">Previous Balance</th>
                <th className="smm">Current Balance</th>
                <th>Details</th>
                <th>Created By</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredBankReports.map((report, inx) => {
                const dateString = report.createdAt;
                const formattedDate = new Date(dateString).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "2-digit",
                });
                return (
                  <tr key={report._id} className="taData">
                    <td>{inx + 1}</td>
                    <td>{report?.customer?.businessName}</td>
                    <td>{report?.bank?.bankName}</td>
                    <td className="smm">{report?.amountIn}</td>
                    <td className="smm">{report?.amountOut}</td>
                    <td className="smm">{report?.balance?.previous}</td>
                    <td className="smm">{report?.balance?.current}</td>
                    <td>{report?.details}</td>
                    <td>{report?.staff?.name}</td>
                    <td>{formattedDate}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="m-auto text-center px-4 py-4">
            <h1>No data available.</h1>
            <p>Please check back later or adjust your filters.</p>
          </div>
        )}
      </div>

      <TablePagination
        rowsPerPageOptions={[15, 25, 50]}
        component="div"
        count={getBankReport.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <div className="dowBtn">
        <button onClick={generatePDF}>Print Report</button>
        <button onClick={exportToExcel}>Export</button>
      </div>
    </div>
  );
};

export default BankReport;
